/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Link } from "react-router-dom";
import { Heading, Text } from "@nordcloud/gnui";
import { ROUTES } from "~/routing/routes";

export const lastChange = "17022023";

export const releaseNotes = [
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator February Release is live!
      </Heading>
      <Date>February 2023</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          📊
        </span>
        {"  "}Cost analysis (Beta version)
      </Text>
      <Text>
        {" "}
        The new feature provides you with a comprehensive and detailed view of
        your cloud costs. With this feature, you can now analyze your total
        costs from various angles, including for each organizational unit,
        application, and environment, as well as in a separate &quot;cost
        analysis&quot; tab located in the menu. Please note that the application
        &quot;cost analysis&quot; tab replaces the charts for anomaly costs, but
        the anomaly cost chart is still available within the cost analysis
        categories in the dropdown menu.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          📈
        </span>
        {"  "}Enhanced EC2 Instance View (AWS)
      </Text>
      <Text>
        {" "}
        With this feature enhancement, we&apos;ve made EC2 resources more
        visible and arranged metadata to improve the user experience.
        Furthermore, we&apos;ve added functionality to help you compare instance
        types and track EC2 metrics more efficiently. By monitoring your
        instances, you can gain a better understanding of how your web
        application or service is performing.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator January Release is live!
      </Heading>
      <Date>January 2023</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🔔
        </span>
        {"  "}Workflows
      </Text>
      <Text>
        {" "}
        With this feature, you can easily create custom notifications for
        budgets, costs, KPIs, unallocated resources, savings suggestions, and
        anomalies, tailored to your specific needs. Notifications can be sent
        through multiple channels, including email, Slack, and ServiceNow, with
        various triggers available to suit your preferences. These automated
        notifications help you maintain control of your spending and ensure that
        you never miss out on important information with no need to log in to
        the product.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator November Release is live!
      </Heading>
      <Date>November 2022</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          💲
        </span>
        {"  "}Reservations and Commitments (AWS)
      </Text>
      <Text>
        {" "}
        This feature makes it easy to understand and achieve the most savings by
        managing your Reserved Instances and Savings Plans through a global view
        of your reservations and commitments. You can keep track of existing
        reserved instances and savings plans, and see recommendations to help
        them avoid unnecessary expenses.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🏷️
        </span>
        {"  "}VPC and SG-based cost and resource allocation
      </Text>
      <Text>
        {" "}
        By utilizing discovery rules based on VPC and Security Group, you can
        easily assign resources from a specific VPC/subnet or Security Group to
        a designated application environment. This feature allows for more
        accurate cost attribution to the correct team or department.
        Additionally, engineers now have the power to utilize VPCs and SGs as
        boundaries for services, applications, or components, ensuring optimal
        security and access control measures.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator August Release is live!
      </Heading>
      <Date>August 2022</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          ⚙️
        </span>
        {"  "}Turbonomic and IBM&nbsp;Multicloud&nbsp;Accelerator Integration
      </Text>
      <Text>
        {" "}
        You can now match estate records in IBM&nbsp;Multicloud&nbsp;Accelerator
        with resources in Turbonomic thanks to the integration of Turbonomic and
        IBM&nbsp;Multicloud&nbsp;Accelerator.
        IBM&nbsp;Multicloud&nbsp;Accelerator will show you the saving cost
        action types from Turbonomic as a saving suggestion. The first
        synchronization with Turbonomic will be initiated once the new
        integration is created. It will allow you to take more actions toward
        your savings goals.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🔎
        </span>
        {"  "}Resource scan date visibility
      </Text>
      <Text>
        {" "}
        You can now monitor the lifespan of your cloud resources in
        IBM&nbsp;Multicloud&nbsp;Accelerator. You can see when
        IBM&nbsp;Multicloud&nbsp;Accelerator scanners first discovered a
        particular resource and when it was last seen by
        IBM&nbsp;Multicloud&nbsp;Accelerator scanners in its respective cloud
        for each estate record. It enables you to find resources that have
        existed for a long time or that have only existed for a short time, thus
        increasing the visibility and credibility of the data.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🗄️
        </span>
        {"  "}Multitenant cost allocation
      </Text>
      <Text>
        {" "}
        IBM&nbsp;Multicloud&nbsp;Accelerator now solves one of the major FinOps
        challenge by enabling more accurate attribution of cloud spend to
        business metrics like products, features, etc. The multitenant cost
        allocation feature allows you to spread the cost of a single resource
        (such as databases or virtual machines) across multiple environments.
        From now on, you are able to split the cost of a single resource (such
        as databases or virtual machines) across multiple environments.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator July Release is live!
      </Heading>
      <Date>July 2022</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          📊
        </span>
        {"  "}Budgeting and forecasting feature
      </Text>
      <Text>
        {" "}
        You can now allocate budgets per Organizational Unit, application, and
        environment, as well as review budgets across all of your cloud
        providers. You will also receive budget recommendations based on your
        previous spend, spending estimates, and forecasts. Every month, you can
        compare your spend to your budget to ensure you stay on track.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator May Release is live!
      </Heading>
      <Date>May 2022</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🔎
        </span>
        {"  "}KPIs
      </Text>
      <Text>
        {" "}
        You can now access key performance indicators (KPIs), which are
        pre-defined metrics that show how well a company achieves key business
        objectives. By collaborating with cross-functional teams to quantify and
        measure the impact of the metrics, you can quickly gain buy-in,
        highlight common-shared goals, and move quickly. KPIs are frequently
        used in FinOps to raise awareness and initiate the cost-cutting process
        within the organization.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🗂
        </span>
        {"  "}GCP service level cost allocation
      </Text>
      <Text>
        {" "}
        GCP customers now have access to a slew of brand new GCP allocation
        features! The visibility has been improved, and there is now a
        &apos;drill-down&apos; option. You can now see the cost of your GCP
        projects on the estate record list, giving you greater cost granularity.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator April Release is live!
      </Heading>
      <Date>April 2022</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        {"  "}Waste information in the organization unit
      </Text>
      <Text>
        {" "}
        Now, on the Organizational Unit detail page, you can see aggregated
        information on the value of its savings (waste) for each application and
        its environment. At the Organizational Unit level, you see the current
        waste value for the current period and the most recent waste value for
        the last period. From now instead of drilling down to each environment
        you have easy access to aggregated data about each application.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🗄
        </span>
        {"  "}&apos;Friendly&apos; account names
      </Text>
      <Text>
        {" "}
        Thanks to the changes we have made, you will be able to see the real
        names of the accounts that have been included in the system - not just
        their numbers. This will make it easier to identify accounts in the
        system.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator March Release is live!
      </Heading>
      <Date>March 2022</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🌱
        </span>
        {"  "}Sustainability for AWS
      </Text>
      <Text>
        {" "}
        IBM&nbsp;Multicloud&nbsp;Accelerator supports cloud footprint
        calculations and savings suggestions for selected AWS resources. From
        now on, you also have access to right-sizing suggestions for reducing
        CO2 emissions.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🗄
        </span>
        {"  "}New filtering options
      </Text>
      <Text>
        {" "}
        Filtering using categories and creating a combination of predefined
        filters are now available! Using a resource categories filter gives you
        the ability to narrow down the list of all resources to a single
        category. You can also create a combination of predefined filters on
        cloud resources and save it. Then you can use it as a view preset.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator February Release is live!
      </Heading>
      <Date>February 2022</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          💸
        </span>
        {"  "}Kubernetes cost visibility
      </Text>
      <Text>
        {" "}
        Now you can not only onboard the Kubernetes resources but also see its
        costs and potential savings.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          📊
        </span>
        {"  "}New filtering options
      </Text>
      <Text>
        {" "}
        We recently introduced new features to make reporting easier. We have
        added a metadata key and value filter, which allow to narrow down the
        list of resources to those that have exact matching pairs of metadata
        key values. Once filtered, you can now export the list to a CSV file.
        This downloads all the data for the resources on the list and send it to
        your mailbox - the CSV report is sent to your email inbox within
        minutes.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          📉
        </span>
        {"  "}OpenShift and Kubernetes cost visibility
      </Text>
      <Text>
        {" "}
        Now with IBM&nbsp;Multicloud&nbsp;Accelerator, you are able to
        automatically discover and map cloud resources to their corresponding
        Kubernetes and OpenShift nodes. As a result, you can see the cost of
        your Kubernetes and OpenShift nodes without having to do any manual
        work. When IBM&nbsp;Multicloud&nbsp;Accelerator knows the costs of the
        nodes, pods costs are automatically calculated based on CPU and Memory
        usage.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator January Release is live!
      </Heading>
      <Date>January 2022</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🌱
        </span>
        {"  "}CO2e Savings
      </Text>
      <Text>
        {" "}
        Now you are able to assess your environmental impact and minimize your
        carbon footprint of your resources. The aggregated information about CO2
        emissions are already visible on the application, environment and estate
        record details view. Now it is all about taking it into account when
        planning and executing your IT operations.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator December Release is live!
      </Heading>
      <Date>December 2021</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🔌
        </span>
        {"  "}External integrations APIs
      </Text>
      <Text>
        {" "}
        That means you can now bring your data and tailor your experience to
        your needs. We expose a set of APIs for enriching your assets within
        IBM&nbsp;Multicloud&nbsp;Accelerator.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          💰
        </span>
        {"  "}Rightsize your VMs
      </Text>
      <Text>
        {" "}
        For Azure & AWS, IBM&nbsp;Multicloud&nbsp;Accelerator now allows you to
        enable rightsizing for underutilized VMs. Based on the
        IBM&nbsp;Multicloud&nbsp;Accelerator Savings Suggestions, you can see
        which of your VMs are underutilized together with the savings amount.
        Now it’s all about following the recommendations to generate the savings
        for your company.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator November Release is live!
      </Heading>
      <Date>November 2021</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🔎
        </span>
        {"  "}Saving suggestions - idle databases
      </Text>
      <Text>
        {" "}
        We’ve added new ways to optimize your cloud cost. Our cost saving
        suggestions now include recommendations that help you find databases
        with no connections for the specified period of time for AWS and Azure.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator October Release is live!
      </Heading>
      <Date>October 2021</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🚀
        </span>
        {"  "}IBM&nbsp;Multicloud&nbsp;Accelerator gains IBM superpowers
      </Text>
      <Text>
        {" "}
        IBM Cloud users can now classify resources and their costs by leveraging
        our unique showback system and gain full visibility into their
        workloads. We are the only tool on the market that supports daily
        granularity of IBM Cloud cost. Just follow the onboarding instructions
        for IBM clouds in our documentation and onboard your IBM cloud accounts
        via our Cloud Accounts tab.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator September Release is live!
      </Heading>
      <Date>September 2021</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          📉
        </span>
        {"  "}OpenShift resources in IBM&nbsp;Multicloud&nbsp;Accelerator
      </Text>
      <Text>
        {" "}
        Just the same as with Kubernetes clusters, you can now use a simple UI
        to onboard your OpenShift clusters to
        IBM&nbsp;Multicloud&nbsp;Accelerator, and enjoy having all your Cloud
        and OpenShift data in one data plane. We adapted the mapping mechanism
        to work automatically with OpenShift resources to carry out the
        discovery work. To see the full picture of what’s in your clouds, you
        can assign your OpenShift resources to applications, environments and
        Business Contexts. In addition, you can filter OpenShift resources in
        the Estate Records view (as a whole), and in the environment details in
        the Estate Records view.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator August Release is live!
      </Heading>
      <Date>August 2021</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🗄
        </span>
        {"  "}Resource group level tags (Azure)
      </Text>
      <Text>
        {" "}
        The latest major improvement for Azure lets you map an Azure resource
        group to applications and environments in
        IBM&nbsp;Multicloud&nbsp;Accelerator by using tags. You can now set up
        IBM&nbsp;Multicloud&nbsp;Accelerator to propagate tags on the level of
        resource groups to each resource in the group. If an Estate Record has
        already been allocated the same tag name as the resource group, the
        value from the Estate Record’s tag takes precedence.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          💰
        </span>
        {"  "}Automatic drift approval
      </Text>
      <Text>
        {" "}
        So, you don’t like the process of manually accepting, or rejecting,
        drifts caused by changes in tags. We get it. From now on, if you want to
        take on-trust all the changes in your cloud estate, you can set up
        IBM&nbsp;Multicloud&nbsp;Accelerator to automatically approve any
        drifts, without IBM&nbsp;Multicloud&nbsp;Accelerator checking-in with
        you first.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator July Release is live!
      </Heading>
      <Date>July 2021</Date>
      <Text>Here is the summary of what has changed:</Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🗄
        </span>
        {"  "}New Estate Records
      </Text>
      <Text>
        {" "}
        Check out the latest improvements for estate records. Now you can sort
        the list of your estate records by monthly cost, type, or region. We
        also improved the filtering mechanism by extending the filtering options
        and adding multiple filters. When using
        IBM&nbsp;Multicloud&nbsp;Accelerator on a daily basis, you will quickly
        appreciate the new sorting criteria and fast, accurate filters.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          💰
        </span>
        {"  "}Reserved Instances visibility
      </Text>
      <Text>
        {" "}
        Now you can manage your AWS reservations using Reserved Instances
        visibility. For clients using AWS, reservations are visible as a
        separate tab in the main menu. Here you will find information about your
        reservations, together with reserved instances coverage and global
        utilization. For coverage, you can set the coverage target according to
        your company’s policy.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          📉
        </span>
        {"  "}Kubernetes resources in IBM&nbsp;Multicloud&nbsp;Accelerator
      </Text>
      <Text>
        {" "}
        We know that you have been waiting for this functionality{" "}
        <span role="img" aria-label="smile">
          🤩
        </span>
        . Now you can use a simple UI to onboard your Kubernetes clusters to
        IBM&nbsp;Multicloud&nbsp;Accelerator, and enjoy having all your Cloud
        and Kubernetes data in one data plane. We adapted the mapping mechanism
        to work automatically with Kubernetes resources and do the discovery
        work, so you can spend your time on more important tasks. To see the
        full picture of what’s in your clouds, filter and assign your Kubernetes
        resources to applications, environments and Business Contexts.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator January Release is live!
      </Heading>
      <Date>January 2021</Date>
      <Text>
        Improving your IBM&nbsp;Multicloud&nbsp;Accelerator experience was one
        of our main focuses for the last two months. Here is the summary of what
        has changed:
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🗄
        </span>
        {"  "}New Mapping Rules
      </Text>
      <Text>
        {" "}
        Check out our newest improvements according to mapping rules on the
        environment level. Now you can be much more specific and create even
        more combinations. In the process of adding a new mapping rule, you will
        be able to see how many resources it will apply to. Our new, improved
        user interface here is just a masterpiece.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          💰 Budgets
        </span>
        {"  "}Budgets
      </Text>
      <Text>
        {" "}
        Now you can control your spending on the application and environment
        level by setting the budgets. This functionality is visible next to your
        Monthly Forecast in the Cost area for each application and environment.
        IBM&nbsp;Multicloud&nbsp;Accelerator will inform you by email when the
        budget is exceeded. Email message will contain the direct link and allow
        you to take immediate action, to better control cloud spend. Budgets
        make unpredictable, predictable.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          📉
        </span>
        {"  "}Archive
      </Text>
      <Text>
        {" "}
        We also improved the IBM&nbsp;Multicloud&nbsp;Accelerator reporting
        area. Now, next to having the available and editable data for this and
        previous month, you will be able to download the archive report with the
        data for previous months in the .csv format. This important
        functionality is visible in the main menu under{" "}
        <Link to={ROUTES.archive}>Archive</Link>.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          👨🏻‍🎨
        </span>
        {"  "}
        UX/UI improvements
      </Text>
      <Text>
        We work on a daily basis to make IBM&nbsp;Multicloud&nbsp;Accelerator
        even more intuitive and helpful as your tool to support you with your
        daily Cloud activities. Within the last two months,
        IBM&nbsp;Multicloud&nbsp;Accelerator changed a lot: refreshed views of
        applications, environments, Business Contexts, more charts and better
        data presentation.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        We are pleased to inform you that the
        IBM&nbsp;Multicloud&nbsp;Accelerator November Release is live!
      </Heading>
      <Date>November 2020</Date>
      <Text>
        This month our main focus was on improving the core functionalities in
        IBM&nbsp;Multicloud&nbsp;Accelerator.
        <br />
        <br />
        We have implemented a Role Based Access Control System, to allow you to
        invite your colleagues to IBM&nbsp;Multicloud&nbsp;Accelerator. With
        this feature you have an opportunity to empower people in your
        organisation by bringing transparency on their spend in the cloud. We
        enhanced data availability for you, now you can download a list of your
        resources not only through API but also directly to CSV file with just
        one single click. We understand how important security is nowadays. We
        not only implemented two factor authentication in
        IBM&nbsp;Multicloud&nbsp;Accelerator but also made the login process
        more secure.{" "}
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          👩‍💼
        </span>
        {"  "}Role Based Access Control
      </Text>
      <Text>
        {" "}
        Yes, from now on you can give IBM&nbsp;Multicloud&nbsp;Accelerator
        access to your colleagues directly from the user interface. This means
        that you can provide user access exactly as it is needed by giving a
        full or restricted access. Finally it &apos;s your call which
        application resources and cost information should be visible by users,
        when you define restricted access you can indicate applications. The new
        feature is available in the general menu under{" "}
        <Link to={ROUTES.settings.users}>Users and Permissions</Link>.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🖥
        </span>
        {"  "}Generate CSV Reports
      </Text>
      <Text>
        On the{" "}
        <Link to={ROUTES.estateRecords.index}>list of estate records</Link>, you
        will find a new button &quot;Export to CSV&quot; which allows you to
        download the current list of records with all essential information. Go
        and try the feature by yourself, from the main menu pick up estate
        records, click on the button and get the list of the resources in your
        inbox (hint: if you would like to narrow the list, use filtering options
        available on this screen).
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          👨🏻‍🎨
        </span>
        {"  "}
        UX/UI improvements
      </Text>
      <Text>
        Every day we work to make IBM&nbsp;Multicloud&nbsp;Accelerator even more
        intuitive and helpful as your tool to support you with your daily Cloud
        activities. Within the last few weeks,
        IBM&nbsp;Multicloud&nbsp;Accelerator changed a lot: More colors,
        graphics, better organized data information, faster and more fluent
        work.
      </Text>
    </>
  ),
  () => (
    <>
      <Heading level={4} mb={0}>
        Check out our latest IBM&nbsp;Multicloud&nbsp;Accelerator updates!
      </Heading>
      <Date>October 2020</Date>
      <Text>
        We are constantly working to improve your
        IBM&nbsp;Multicloud&nbsp;Accelerator experience, here is the summary of
        what has changed...
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          📊
        </span>
        General dashboard
      </Text>
      <Text>
        this is something that you will definitely not miss -{" "}
        <Link to={ROUTES.index}>
          the main IBM&nbsp;Multicloud&nbsp;Accelerator dashboard
        </Link>{" "}
        is completely changed. From now on you can find there general
        information about your current spend and monthly forecast, information
        about costs divided by cloud providers, top spending applications and
        other useful information.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          💸
        </span>
        Billing periods
      </Text>
      <Text>
        current and previous billing period data are not only available but also
        editable. Now you can add applications, change your assignments, edit
        Organizational Units, add mapping rules and many more not only within
        the current billing period but also for the previous one.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          👩‍💻
        </span>
        API playground
      </Text>
      <Text>
        you can now use IBM&nbsp;Multicloud&nbsp;Accelerator through the API.{" "}
        <Link to={ROUTES.api}>
          Full API documentation is available in our UI
        </Link>
        . You can find IBM&nbsp;Multicloud&nbsp;Accelerator API playground after
        clicking in the user name in the right top screen corner.
      </Text>
      <Text mb={0} weight="medium">
        <span role="img" aria-label="API">
          🛠
        </span>
        UI/UX improvements
      </Text>
      <Text>
        we are constantly working to make IBM&nbsp;Multicloud&nbsp;Accelerator
        more intuitive and user friendly. Within the last few weeks
        IBM&nbsp;Multicloud&nbsp;Accelerator changed a lot: more colours,
        graphics, better organized data information, faster and more fluent
        work.
      </Text>
    </>
  ),
];

function Date(props: React.ComponentPropsWithoutRef<typeof Text>) {
  return (
    <Text
      mt={0}
      weight="medium"
      as="time"
      style={{ display: "block" }}
      {...props}
    />
  );
}
